/** Die Finanzdaten, die der Überblick und die Tabs gemeinsam brauchen (BE-05). */
import { useEffect } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '@/db/schema';
import { abosAbrechnen } from '@/db/repositories/finanzen';
import { sparzielAus, sparrateAusVerlauf, type Sparziel } from '@/domain/finanzen/sparziele';
import { aboZeilen, budgetStand, guthaben, letzteMonate, monatsSumme, monatVon, tagesbudget } from '@/domain/finanzen/finanzen';
import type { Account, Envelope, Subscription, Transaction } from '@/domain/types';
import { today } from '@/lib/datetime';

const leer = { konten: [] as Account[], tx: [] as Transaction[], kategorien: [] as Envelope[], abos: [] as Subscription[], sparziele: [] as Sparziel[] };

export function useFinanzen() {
  const heute = today();
  const monat = monatVon(heute);
  // B-02: faellige Abos/Dauerauftraege eintragen, sobald irgendeine Finanz-Ansicht offen ist.
  useEffect(() => { void abosAbrechnen(heute); }, [heute]);
  const d = useLiveQuery(async () => {
    const [konten, tx, kategorien, abos, zeilen] = await Promise.all([db.accounts.toArray(), db.transactions.orderBy('date').toArray(), db.envelopes.toArray(), db.subscriptions.toArray(), db.oberflaeche.where('id').startsWith('sparziel:').toArray()]);
    return { konten, tx, kategorien, abos, sparziele: zeilen.flatMap((z) => sparzielAus(z) ?? []) };
  }, []);
  const { tx, kategorien, abos, sparziele } = d ?? leer;
  const konten = [...(d ?? leer).konten].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const stand = budgetStand(kategorien, tx, monat, heute);
  return {
    geladen: d !== undefined, heute, monat, konten, tx, kategorien, abos, sparziele, stand,
    guthaben: guthaben(konten, tx),
    summe: monatsSumme(tx, monat),
    verlauf: letzteMonate(tx, heute, 6),
    /** Schnitt der letzten drei VOLLEN Monate (der laufende ist noch nicht abgeschlossen). */
    schnittSparrate: sparrateAusVerlauf(letzteMonate(tx, heute, 4).slice(0, 3)),
    tagesbudget: tagesbudget(stand, heute),
    abosSortiert: aboZeilen(abos, heute),
  };
}

export type FinanzDaten = ReturnType<typeof useFinanzen>;
